import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Badge } from "react-bootstrap";
import { shareReport, getTeachers } from "../services/APIClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import "./ShareTeachersModal.css";

const ShareTeachersModal = ({
  show,
  handleClose,
  assessmentName,
  className,
  rollNumber,
  senderName,
  teacherId,
  schoolId,
  assessmentId,
  selectedStudentIds = [],
  // These props come from ShareActionCard
  isParentView = false,
  isTeacherView = false,
  isDetailedView: initialDetailedView,
  // The studentsList prop may be empty, so we handle both possibilities
  activeAccordionStudents = [],
}) => {
  // State for the teacher search and selection
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  console.log(assessmentId, "assessmentId in ShareTeachersModal");

  // Initialize the detailed view state based on props
  const [isDetailedView, setIsDetailedView] = useState(() => {
    if (isTeacherView) return true;
    if (isParentView) return false;
    return initialDetailedView !== undefined ? initialDetailedView : true;
  });

  // Update the detailed view state when view mode props change
  useEffect(() => {
    if (isTeacherView) {
      setIsDetailedView(true);
    } else if (isParentView) {
      setIsDetailedView(false);
    }
  }, [isTeacherView, isParentView]);

  // Fetch all teachers when the modal opens
  useEffect(() => {
    if (show && !showConfirmation) {
      fetchTeachers();
    }
  }, [show, schoolId]);

  // Fetch teachers from the API
  const fetchTeachers = async () => {
    if (!schoolId) return;

    setIsLoading(true);
    try {
      const teachers = await getTeachers(schoolId);
      console.log("Fetched teachers:", teachers);
      setAllTeachers(teachers);
    } catch (error) {
      console.error("Error fetching teachers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input changes - filter locally
  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase().trim();
    setSearchQuery(query);

    if (query.length >= 2) {
      // Filter teachers locally based on the search query
      const filteredTeachers = allTeachers.filter(
        (teacher) =>
          teacher.name.toLowerCase().includes(query) ||
          (teacher.email && teacher.email.toLowerCase().includes(query))
      );
      console.log("Search query:", query);
      console.log("Filtered teachers:", filteredTeachers);
      setSearchResults(filteredTeachers);
    } else {
      setSearchResults([]);
    }
  };

  // Select a teacher from search results
  const handleSelectTeacher = (teacher) => {
    console.log("Selected teacher:", teacher);
    if (!selectedTeachers.some((t) => t.id === teacher.id)) {
      setSelectedTeachers((prev) => {
        const newSelection = [...prev, teacher];
        console.log("Updated selected teachers:", newSelection);
        return newSelection;
      });
      setSearchQuery("");
      setSearchResults([]);
    }
  };

  // Remove a selected teacher
  const handleRemoveTeacher = (teacherId) => {
    setSelectedTeachers((prev) =>
      prev.filter((teacher) => teacher.id !== teacherId)
    );
  };

  // Reset state when modal is closed
  useEffect(() => {
    if (!show) {
      setSearchQuery("");
      setSearchResults([]);
      // Don't reset selectedTeachers here - only after successful submit
      setShowConfirmation(false);
    }
  }, [show]);

  // Handle the first step confirmation (proceed to confirmation modal)
  const handleProceedToConfirmation = () => {
    setShowConfirmation(true);
  };

  // Handle back button from confirmation
  const handleBackFromConfirmation = () => {
    setShowConfirmation(false);
  };

  // Handle the final share action
  const handleConfirmShare = async () => {
    setIsSharing(true);
    try {
      if (selectedTeachers.length === 0) {
        throw new Error("No teachers selected as recipients");
      }

      if (!teacherId) {
        throw new Error("Missing sender teacher ID");
      }

      if (!schoolId) {
        throw new Error("Missing school ID");
      }

      if (!assessmentId) {
        throw new Error("Missing assessment ID");
      }

      if (!selectedStudentIds || selectedStudentIds.length === 0) {
        throw new Error("No students selected");
      }

      // Get teacher IDs instead of emails (let the backend resolve the emails)
      const recipientTeacherIds = selectedTeachers.map((teacher) => teacher.id);

      console.log("Sharing with teacher IDs:", recipientTeacherIds);
      console.log("Assessment ID:", assessmentId);
      console.log("Selected student IDs:", selectedStudentIds);
      console.log("Detailed view:", isDetailedView);

      // Call the shareReport API with teacher IDs
      // Note: You may need to update your API to accept teacher IDs instead of emails
      await shareReport(
        teacherId,
        schoolId,
        assessmentId,
        assessmentName,
        selectedStudentIds,
        recipientTeacherIds, // Pass IDs instead of emails
        isDetailedView
      );

      // Show success message
      alert("Reports shared successfully!");
      // Reset teachers after successful share
      setSelectedTeachers([]);
      setShowConfirmation(false);
      handleClose();
    } catch (error) {
      console.error("Error sharing reports:", error);
      alert(`Failed to share reports: ${error.message}`);
    } finally {
      setIsSharing(false);
    }
  };

  // Render the confirmation modal
  const renderConfirmationModal = () => {
    return (
      <Modal
        show={show && showConfirmation}
        onHide={handleBackFromConfirmation}
        size="lg"
        centered
        className="share-confirmation-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Sharing</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Report Summary Card */}
          <div className="report-card">
            <h5 className="report-title mb-2">
              Report Summary
              <Badge className="report-badge ms-2">
                {selectedStudentIds.length} Students
              </Badge>
            </h5>

            <div className="report-details">
              <div className="report-detail-item">
                <span className="detail-label">Assessment</span>
                <span className="detail-value">{assessmentName || "N/A"}</span>
              </div>

              <div className="report-detail-item">
                <span className="detail-label">Class</span>
                <span className="detail-value">{className || "N/A"}</span>
              </div>

              <div className="report-detail-item">
                <span className="detail-label">Recipients</span>
                <span className="detail-value">
                  {selectedTeachers.length} teachers
                </span>
              </div>
            </div>

            <div className="detailed-view-switch mt-3">
              <Form.Check
                type="switch"
                id="detailedViewConfirm"
                label={
                  isDetailedView
                    ? "Detailed View (includes all assessment details)"
                    : "Simple View (basic screener information)"
                }
                checked={isDetailedView}
                onChange={() => setIsDetailedView(!isDetailedView)}
              />
            </div>
          </div>

          {/* 
          // Selected Teachers Section - COMMENTED OUT
          <h5 className="section-title mt-4">
            Selected Teachers ({selectedTeachers.length})
          </h5>
          
          <div className="recipient-grid">
            {selectedTeachers.map((teacher) => (
              <div key={teacher.id} className="recipient-card">
                <div className="recipient-info">
                  <div className="recipient-name">{teacher.name}</div>
                  <div className="recipient-role">
                    {teacher.additionalRoles && teacher.additionalRoles.length > 0 
                      ? teacher.additionalRoles.join(', ')
                      : "Teacher"}
                  </div>
                </div>
              </div>
            ))}
          </div>
          */}

          {/*
          // Selected Students Section - COMMENTED OUT
          <h5 className="section-title mt-4">
            Selected Students ({selectedStudentIds.length})
          </h5>
          
          {selectedStudentIds.length > 0 ? (
            <div className="student-grid">
              {activeAccordionStudents
                .filter(student => selectedStudentIds.includes(student.id))
                .map((student) => (
                  <div key={student.id} className="student-card">
                    <div className="student-name">{student.name}</div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="no-results">
              No students selected. Please select at least one student to share the report.
            </div>
          )}
          */}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <Button
              variant="light"
              onClick={handleBackFromConfirmation}
              disabled={isSharing}
              className="back-btn"
            >
              Back
            </Button>
          </div>
          <div>
            <button
              onClick={handleConfirmShare}
              disabled={
                selectedTeachers.length === 0 ||
                selectedStudentIds.length === 0 ||
                isSharing
              }
              className="share-btn blue-action-button"
            >
              {isSharing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sharing...
                </>
              ) : (
                `Share Report`
              )}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  // Render the teacher search modal (initial step)
  const renderTeacherSearchModal = () => {
    return (
      <Modal
        show={show && !showConfirmation}
        onHide={handleClose}
        size="lg"
        centered
        className="share-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            Share Results
            <div className="detailed-view-switch ms-3">
              <Form.Check
                type="switch"
                id="detailedView"
                label={
                  isDetailedView
                    ? "Detailed View (includes all assessment details)"
                    : "Simple View (basic screener information)"
                }
                checked={isDetailedView}
                onChange={() => setIsDetailedView(!isDetailedView)}
              />
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Teacher Search Section */}
          <h5 className="section-title text-center">Search for Teachers</h5>

          <div className="search-container">
            <div className="search-input-wrapper">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <Form.Control
                type="text"
                placeholder="Search for teachers by name"
                value={searchQuery}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>

            {isLoading && (
              <div className="search-loading text-center my-2">
                <Spinner animation="border" size="sm" /> Loading teachers...
              </div>
            )}

            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((teacher) => (
                  <div
                    key={teacher.id}
                    className="search-result-item"
                    onClick={() => handleSelectTeacher(teacher)}
                  >
                    <div className="search-result-name">{teacher.name}</div>
                    <div className="search-result-role">
                      {teacher.additionalRoles &&
                      teacher.additionalRoles.length > 0
                        ? teacher.additionalRoles[0]
                        : "Teacher"}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {searchQuery.trim().length >= 2 &&
              searchResults.length === 0 &&
              !isLoading && (
                <div className="no-results">
                  No teachers found matching "{searchQuery}"
                </div>
              )}
          </div>

          {/* Selected Teachers Section */}
          {selectedTeachers.length > 0 && (
            <div className="selected-teachers mb-4">
              <h5 className="section-title mt-4">
                Selected Teachers ({selectedTeachers.length})
              </h5>
              <div className="recipient-grid">
                {selectedTeachers.map((teacher) => (
                  <div key={teacher.id} className="recipient-card">
                    <div className="recipient-info">
                      <div className="recipient-name">{teacher.name}</div>
                      <div className="recipient-role">
                        {teacher.additionalRoles &&
                        teacher.additionalRoles.length > 0
                          ? teacher.additionalRoles.join(", ")
                          : "Teacher"}
                      </div>
                    </div>
                    <Button
                      variant="light"
                      size="sm"
                      className="text-danger"
                      onClick={() => handleRemoveTeacher(teacher.id)}
                    >
                      ✕
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected Students Section */}
          <h5 className="section-title mt-4">
            Selected Students ({selectedStudentIds.length})
          </h5>

          {selectedStudentIds.length > 0 ? (
            <div className="student-grid">
              {activeAccordionStudents
                .filter((student) => selectedStudentIds.includes(student.id))
                .map((student) => (
                  <div key={student.id} className="student-card">
                    <div className="student-name">{student.name}</div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="no-results">
              No students selected. Please select at least one student to share
              the report.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <Button
              variant="light"
              onClick={handleClose}
              className="cancel-btn"
            >
              Cancel
            </Button>
          </div>
          <div>
            <button
              variant="primary"
              onClick={handleProceedToConfirmation}
              disabled={
                selectedTeachers.length === 0 || selectedStudentIds.length === 0
              }
              className="next-btn blue-action-button"
            >
              {`Share with ${selectedTeachers.length} recipient${
                selectedTeachers.length !== 1 ? "s" : ""
              }`}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  // Render the appropriate modal based on the current step
  return (
    <>
      {renderTeacherSearchModal()}
      {renderConfirmationModal()}
    </>
  );
};

export default ShareTeachersModal;
